import { RSAA } from 'redux-api-middleware'
import {
  createDoctorImageUrl,
  getApiPath,
  makeQueryParams,
} from 'sidecar-utility-library'
import { makePaginationParams } from 'sidecar-utilities'

import { types, initialState } from './types'
import { mergeArraysRemovingDuplicates } from '../utils/mergeArraysRemovingDuplicates'
import { isStaleResponse } from './utils/isStaleResponse'

/**
 * Meta stamped on every phase of a doctor-rates request so the reducer can tell
 * which context the arriving rates were requested for.
 *
 * A context key rather than a per-call nonce, because the rates are paginated:
 * the key has to stay stable across the "show more" calls that are meant to
 * merge into the same list, while changing whenever the list itself should
 * start over.
 *
 * Both halves are needed. The BE issues a new `monitoringId` whenever the care
 * item, its filters or the map bounds change, but individual providers and
 * practices are fetched from different endpoints through the _same_ action
 * types, and nothing guarantees they get different monitoringIds — so without
 * `providerScope` in the key a doctors response could merge into a practices
 * list.
 *
 * @param {'providers' | 'practices'} providerScope - Which endpoint the rates
 *   were requested from.
 * @param {string} [monitoringId] - Monitoring id for the current care context.
 * @returns {Object} Meta for an RSAA type descriptor.
 */
const buildRateContextMeta = (providerScope, monitoringId) => ({
  rateContext: `${providerScope}:${monitoringId ?? 'none'}`,
})

export const fetchSimpleDoctorRates = ({
  memberUuid,
  sidecarCode,
  receiveAt,
  specialties,
  zipCode,
  state,
  lat,
  lon,
  radius,
  priceSort,
  monitoringId,
  sessionId,
  intentCallId,
  neLat,
  neLon,
  swLat,
  swLon,
}) => ({
  [RSAA]: {
    endpoint: ({ Care: { careRates } }) => {
      return getApiPath(
        'doc',
        `rates/cares/doctors${makeQueryParams({
          memberUuid,
          sidecarCode,
          receiveAt,
          specialties,
          zipCode,
          state,
          lat,
          lon,
          radius,
          priceSort,
          monitoringId,
          sessionId,
          intentCallId,
          neLat,
          neLon,
          swLat,
          swLon,
          ...makePaginationParams(careRates),
        })}`,
        'v2',
      )
    },
    method: 'GET',
    types: [
      {
        type: types.FETCH_DOCTOR_RATES_REQUEST,
        meta: buildRateContextMeta('providers', monitoringId),
      },
      {
        type: types.FETCH_DOCTOR_RATES_SUCCESS,
        payload: (action, state, res) => {
          // Extract apiCallId from response header for analytics
          const apiCallId = res.headers.get('x-api-call-id')

          return res.json().then(json => ({
            ...json,
            apiCallId,
          }))
        },
        meta: {
          ...buildRateContextMeta('providers', monitoringId),
          predicate: (a, b) => a.npi === b.npi,
          npiField: 'npi',
        },
      },
      {
        type: types.FETCH_DOCTOR_RATES_ERROR,
        meta: buildRateContextMeta('providers', monitoringId),
      },
    ],
  },
})

export const fetchSimpleDoctorRatesForPractices = ({
  memberUuid,
  sidecarCode,
  receiveAt,
  specialties,
  zipCode,
  state,
  lat,
  lon,
  radius,
  priceSort,
  monitoringId,
  sessionId,
  intentCallId,
  neLat,
  neLon,
  swLat,
  swLon,
}) => ({
  [RSAA]: {
    endpoint: ({ Care: { careRates } }) => {
      return getApiPath(
        'doc',
        `rates/cares/doctors/practices${makeQueryParams({
          memberUuid,
          sidecarCode,
          receiveAt,
          specialties,
          zipCode,
          state,
          lat,
          lon,
          radius,
          priceSort,
          sessionId,
          monitoringId,
          intentCallId,
          neLat,
          neLon,
          swLat,
          swLon,
          ...makePaginationParams(careRates),
        })}`,
        'v2',
      )
    },
    method: 'GET',
    types: [
      {
        type: types.FETCH_DOCTOR_RATES_REQUEST,
        meta: buildRateContextMeta('practices', monitoringId),
      },
      {
        type: types.FETCH_DOCTOR_RATES_SUCCESS,
        payload: (action, state, res) => {
          // Extract apiCallId from response header for analytics
          const apiCallId = res.headers.get('x-api-call-id')

          return res.json().then(json => ({
            ...json,
            apiCallId,
          }))
        },
        meta: {
          ...buildRateContextMeta('practices', monitoringId),
          predicate: (a, b) => a.facilityNpi === b.facilityNpi,
          npiField: 'facilityNpi',
        },
      },
      {
        type: types.FETCH_DOCTOR_RATES_ERROR,
        meta: buildRateContextMeta('practices', monitoringId),
      },
    ],
  },
})

export const updateCareRateBookmark = (npi, bookmarked) => ({
  type: types.UPDATE_CARE_RATE_BOOKMARK,
  payload: { npi, bookmarked },
})

export const clearCareRates = () => ({
  type: types.CLEAR_CARE_RATES,
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.FETCH_DOCTOR_RATES_REQUEST: {
      return {
        ...state,
        careRates: {
          ...state.careRates,
          pricingBusy: true,
          requestKey: meta.rateContext ?? null,
        },
      }
    }
    case types.FETCH_DOCTOR_RATES_SUCCESS: {
      // Rates requested for a care item, filter set or map area the user has
      // since left must not be merged into the list they're looking at now
      // (BASH-219).
      if (isStaleResponse(meta.rateContext, state.careRates.requestKey)) {
        return state
      }

      const { page, size, last, content, apiCallId } = payload
      const isLastPageReached = last || content.length === 0

      const updatedContent =
        content.length > 0
          ? content.map(provider => ({
              ...provider,
              imgUrl: createDoctorImageUrl(provider, meta.npiField),
            }))
          : content

      const predicate =
        typeof meta.predicate === 'function'
          ? meta.predicate
          : (a, b) => a.npi === b.npi

      return {
        ...state,
        careRates: {
          ...state.careRates,
          pricingBusy: false,
          page: page,
          size: size,
          isLastPageReached,
          apiCallId: apiCallId || state.careRates.apiCallId,
          entities: mergeArraysRemovingDuplicates({
            arrayOne: state.careRates.entities,
            arrayTwo: updatedContent,
            predicate,
          }),
          error: '',
        },
      }
    }
    case types.FETCH_DOCTOR_RATES_ERROR: {
      if (isStaleResponse(meta.rateContext, state.careRates.requestKey)) {
        return state
      }

      return {
        ...state,
        careRates: {
          ...state.careRates,
          pricingBusy: false,
          error: payload.response.message,
        },
      }
    }
    case types.UPDATE_CARE_RATE_BOOKMARK: {
      return {
        ...state,
        careRates: {
          ...state.careRates,
          entities: state.careRates.entities.map(provider =>
            provider.npi === payload.npi
              ? { ...provider, bookmarked: payload.bookmarked }
              : provider,
          ),
        },
      }
    }
    case types.CLEAR_CARE_RATES: {
      return {
        ...state,
        careRates: initialState.careRates,
      }
    }

    default:
      return state
  }
}
